import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

import { stockKeys } from "./query-keys";

type Stock = components["schemas"]["Stock"];

interface TransferVars {
  sku: string;
  from_marketplace: "shopee" | "lazada" | "tiktok";
  to_marketplace: "shopee" | "lazada" | "tiktok";
  qty: number;
  /**
   * Optional operator note (PRD v1.8 §4.2) recorded on the audit log. Only sent
   * when non-empty — the backend treats absent/empty as "store nothing".
   */
  remark?: string;
}

export function useTransferStock() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, TransferVars, { snapshot: [readonly unknown[], unknown][] }>({
    mutationFn: async ({ sku, from_marketplace, to_marketplace, qty, remark }) => {
      const trimmed = remark?.trim();
      const { error } = await apiClient.POST("/v1/stock/{sku}/transfer", {
        params: { path: { sku } },
        body: { from_marketplace, to_marketplace, qty, ...(trimmed ? { remark: trimmed } : {}) },
      });
      if (error) throw error;
    },
    onMutate: async ({ sku, from_marketplace, to_marketplace, qty }) => {
      // Cancel any in-flight list queries to avoid overwrites
      await queryClient.cancelQueries({ queryKey: stockKeys.lists });

      // Snapshot all matching list query data for rollback
      const snapshot = queryClient.getQueriesData<{ items?: Stock[] }>({
        queryKey: stockKeys.lists,
      });

      // Apply optimistic update: adjust `sellable` on both marketplace rows for this SKU
      queryClient.setQueriesData<{ items?: Stock[] }>({ queryKey: stockKeys.lists }, (old) => {
        if (!old?.items) return old;
        return {
          ...old,
          items: old.items.map((item) => {
            if (item.sku !== sku) return item;
            if (item.marketplace === from_marketplace) {
              return { ...item, sellable: (item.sellable ?? 0) - qty };
            }
            if (item.marketplace === to_marketplace) {
              return { ...item, sellable: (item.sellable ?? 0) + qty };
            }
            return item;
          }),
        };
      });

      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      // Rollback optimistic updates
      if (ctx?.snapshot) {
        for (const [queryKey, data] of ctx.snapshot) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.lists });
    },
  });
}
